import { useState } from "react";
import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryGrid from "@/pages/home/components/CategoryGrid";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { allNews, hotMovies, latestMovies, shortDate } from "@/lib/catalog";

const PER_PAGE = 12;

export default function Home() {
  useDocumentMeta({
    title: "日本情色电影 - 最新日本情色电影推荐与资讯",
    description:
      "日本情色电影为您提供最新的日本情色电影推荐，涵盖多种风格和经典作品，让您轻松了解相关电影资讯与动态。",
    keywords: "日本情色电影,日本电影,情色电影,日影推荐,日本影视,成人电影,日本电影资讯",
  });

  const [page, setPage] = useState(1);
  const latest = latestMovies();
  const totalPages = Math.ceil(latest.length / PER_PAGE);
  const pageItems = latest.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const hot = hotMovies(8);
  const latestNews = [...allNews].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 3);

  const changePage = (next: number) => {
    setPage(next);
    const anchor = document.getElementById("latest-movies");
    if (anchor) anchor.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <HeroCarousel />

        <section className="w-full border-b border-background-300/50 bg-background-50">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-8 md:py-16">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.4fr_1fr] lg:items-end">
              <div>
                <p className="flex items-center gap-3 text-[11px] tracking-widest2 text-accent-500 font-label">
                  <span className="h-px w-8 bg-accent-500" />
                  ABOUT THE ARCHIVE
                </p>
                <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-50 md:text-4xl">
                  日本情色电影 · 最新推荐与影像资讯
                </h1>
                <p className="mt-4 max-w-2xl text-[13px] leading-relaxed text-foreground-400 md:text-sm">
                  我们以杂志编辑的方式整理日本成人文艺电影的片目：从昭和末年的室内调度，到当下的低照度数字摄影。
                  这里没有播放器，只有封面、片目信息、导演脉络与写作——把作品当作电影来看，而不是当作消费品。
                </p>
              </div>
              <dl className="grid grid-cols-3 gap-4 border-t border-background-300/60 pt-6 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
                {[
                  { label: "收录影片", value: latest.length },
                  { label: "影像分类", value: 6 },
                  { label: "资讯与影评", value: allNews.length },
                ].map((stat) => (
                  <div key={stat.label}>
                    <dt className="text-[11px] text-foreground-500 font-label">{stat.label}</dt>
                    <dd className="mt-1 font-display text-3xl font-medium text-accent-400 md:text-4xl">
                      {String(stat.value).padStart(2, "0")}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100/40">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-8 md:py-16">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-[11px] tracking-widest2 text-accent-500 font-label">CATEGORIES</p>
                <h2 className="mt-2 font-heading text-xl font-bold text-foreground-50 md:text-2xl">分类快捷入口</h2>
              </div>
              <p className="max-w-md text-[12px] leading-relaxed text-foreground-500">
                按题材与年代划分的六个单元，每一类都附有编辑撰写的类型说明。
              </p>
            </div>
            <div className="mt-7">
              <CategoryGrid />
            </div>
          </div>
        </section>

        <section id="latest-movies" className="w-full scroll-mt-24 bg-background-50">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-8 md:py-16">
            <div className="flex flex-wrap items-end justify-between gap-4 border-b border-background-300/60 pb-5">
              <div>
                <p className="text-[11px] tracking-widest2 text-accent-500 font-label">LATEST UPDATES</p>
                <h2 className="mt-2 font-heading text-xl font-bold text-foreground-50 md:text-2xl">最新影片</h2>
              </div>
              <div className="flex items-center gap-3 text-[12px] text-foreground-500 font-label">
                <span>
                  共 {latest.length} 部 · 第 {page} / {totalPages} 页
                </span>
                <Link
                  to="/category/new"
                  className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-accent-400 transition-colors hover:text-accent-300"
                >
                  查看全部
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>

            <div className="mt-7 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {pageItems.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>

            <Pagination page={page} totalPages={totalPages} onChange={changePage} />
          </div>
        </section>

        <section className="w-full border-y border-background-300/50 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-8 md:py-16">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="text-[11px] tracking-widest2 text-accent-500 font-label">MOST WATCHED</p>
                <h2 className="mt-2 font-heading text-xl font-bold text-foreground-50 md:text-2xl">热门推荐</h2>
              </div>
              <p className="max-w-md text-[12px] leading-relaxed text-foreground-500">
                依据站内浏览热度排序（演示数据），评分为编辑示例值，不代表任何真实评价体系。
              </p>
            </div>

            <div className="mt-7 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4">
              {hot.map((movie, index) => (
                <MovieCard key={movie.id} movie={movie} rank={index + 1} showSynopsis={false} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50">
          <div className="mx-auto w-full max-w-[1400px] px-4 py-12 md:px-8 md:py-16">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.6fr]">
              <div>
                <p className="text-[11px] tracking-widest2 text-accent-500 font-label">NEWSROOM</p>
                <h2 className="mt-2 font-heading text-xl font-bold text-foreground-50 md:text-2xl">资讯速览</h2>
                <p className="mt-3 max-w-sm text-[12px] leading-relaxed text-foreground-500">
                  行业动态、修复重映、摄影技术专题与影评。文字是我们唯一使用的媒介。
                </p>
                <Link
                  to="/news"
                  className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300/80 px-4 py-2 text-[12px] text-foreground-200 transition-colors hover:border-accent-500 hover:text-accent-400 font-label"
                >
                  全部资讯
                  <i className="ri-arrow-right-line text-sm" />
                </Link>
              </div>

              <ul className="flex flex-col divide-y divide-background-300/50 border-t border-background-300/50">
                {latestNews.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/news/detail/${item.id}`}
                      className="group flex cursor-pointer flex-col gap-2 py-5 sm:flex-row sm:items-baseline sm:gap-6"
                    >
                      <span className="shrink-0 font-display text-sm text-accent-500">{shortDate(item.date)}</span>
                      <div className="min-w-0">
                        <p className="text-[10px] tracking-widest2 text-foreground-600 font-label">{item.category}</p>
                        <h3 className="mt-1 font-heading text-[14px] font-medium leading-snug text-foreground-100 transition-colors group-hover:text-accent-400">
                          {item.title}
                        </h3>
                        <p className="mt-2 line-clamp-2 text-[12px] leading-relaxed text-foreground-500">{item.excerpt}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}